<template>
  <div class="sum" :class="{ 'sum--completed': isCompleted, 'sum--mobile': isMobile }">
    <div class="sum-label">
      <span class="sum-text">{{ $t('generic.labels.total')}}</span>
      <CiTooltip :infoIconColor="isCompleted && !bookingFailed ? 'white' : 'black'">
        <template slot="title">{{ $t('tooltips.total.title') }}</template>
        <template slot="content">
          <p v-html="$t('tooltips.total.content')"></p>
        </template>
      </CiTooltip>
    </div>
    <div class="sum-value">
      <div v-if="status.loading" class="spinner-container">
        <span class="spinner"></span>
      </div>
      <template v-else>
        <span class="sum-value-main">{{ price.total | currency(campgroundCurrency, language.code) }}</span>
        <span
          class="sum-value-alt d-block"
          v-if="price.currency_alt && userCurrency.code !== campgroundCurrency"
        >{{ price.currency_alt.total | currency(userCurrency.code, language.code) }}</span>
        <span class="sum-value-taxes d-block text-small">{{ $t('generic.labels.incTaxes') }}</span>
      </template>
      <div class="sum-online-info text-small" v-if="paymentSystem === 'ONLINE'">
        {{ $t('generic.onlinePaymentInfo', { amount: $options.filters.currency(price.deposit_value, campgroundCurrency, language.code) }) }}
      </div>
    </div>
    <template v-if="hasPaymentProvider">
      <div class="sum-deposit" v-if="bookingInProgress || bookingSuccess || bookingAndRefundFailed">
        <span v-if="bookingInProgress && depositIsPercent">{{ $t('generic.depositDuePercent', { deposit_percent: price.deposit_percent, deposit_value: depositValue })}}</span>
        <span v-if="bookingInProgress && !depositIsPercent && !depositIsTotal">{{ $t('generic.depositDue', { deposit_value: depositValue })}}</span>
        <span v-if="bookingSuccess && !depositIsTotal">{{ $t('generic.depositPayed', { deposit_value: depositValue })}}</span>
        <span v-if="bookingAndRefundFailed">{{ $t('generic.depositPayedBookingAndRefundFailed', { deposit_value: depositValue })}}</span>
      </div>
    </template>
    <div class="sum-indicator" v-if="isMobile && !isCompleted"></div>
  </div>
</template>

<script>
import { CiTooltip } from '@/components/index'
import { mapGetters } from 'vuex'

export default {
  name: 'CiSum',
  components: { CiTooltip },
  props: {
    bookingFailed: { type: Boolean, default: false, required: false },
    campgroundCurrency: {
      type: String,
      required: true,
    },
    confirmation: { type: Object, required: false },
    language: { type: Object, required: true },
    isCompleted: { type: Boolean, default: false },
    isMobile: { type: Boolean, default: false },
    userCurrency: {
      type: Object,
      required: true,
    },
    price: {
      type: Object,
      required: true,
    },
  },
  computed: {
    ...mapGetters('booking', ['selectedCampsite']),
    ...mapGetters('campground', ['paymentInfos', 'status']),


    paymentSystem() {
      if (!this.selectedCampsite || !this.paymentInfos) return null
      const isAcco = this.selectedCampsite.campsite_type === 'accommodation'
      const info = isAcco ? this.paymentInfos.rentals : this.paymentInfos.pitches
      return info && info.system ? info.system : null
    },

    hasPaymentProvider() {
      return this.paymentSystem === 'ONLINE';
    },

    depositValue() {
      return this.$options.filters.currency(
        this.price.deposit_value,
        this.campgroundCurrency,
        this.language.code
      )
    },

    bookingAndRefundFailed() {
      return this.bookingFailed && this.confirmation && !this.confirmation.refunded
    },

    bookingSuccess() {
      return (
        !this.bookingFailed &&
        this.price.deposit_value > 0 &&
        this.isCompleted &&
        this.confirmation &&
        !this.confirmation.refunded
      )
    },

    bookingInProgress() {
      return this.price.deposit_value > 0 && !this.isCompleted
    },

    depositIsTotal() {
      return this.price.deposit_value === this.price.total
    },

    depositIsPercent() {
      return !this.depositIsTotal && this.price.deposit_value !== this.price.minimum_fixed_deposit
    },
  },
  mounted() {
    // console.log("PRICE:", this.price)
  }
}
</script>
<style lang="scss" scoped>
.sum {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  left: 0;
  margin: 0;
  min-height: 60px;
  /* add horizontal padding to control gutters; remove child side margins to avoid double spacing */
  padding: 15px 15px;
  width: 100%;
  border: 2px solid var(--primary-color);

  .sum-deposit {
    @include font-format(0.875rem, 1.25rem);

    width: 100%;
    margin: 15px 15px 0;
    text-align: right;
  }

  &.sum--completed {
    @include font('gotham_mediumregular');

    background: var(--primary-color);
    color: white;

    .sum-deposit {
      @include font('gotham_bookregular');
    }
  }

  &.sum--mobile {
    background-color: $white;
    bottom: 0;
    position: fixed;
    z-index: 400;

    @include media-breakpoint-up(lg) {
      display: none;
    }
  }

  .sum-label {
    display: flex;
    align-items: center;
    /* moved horizontal spacing to parent .sum */
    margin-left: 0;
    height: 100%;

    .sum-text {
      @include font('gotham_mediumregular');
      @include font-format(0.875rem, 0.875rem);
    }
  }

  .sum-value {
    @include font-format(1.375rem, 1.25em);

    /* moved horizontal spacing to parent .sum */
    margin-right: 0;
    text-align: right;
    display: flex;
    flex-direction: column;
    align-items: flex-end;

    .sum-value-alt {
      @include font-format(1rem, 1.25em);

      color: #545454;
      margin-top: 2px;
    }

    .sum-value-taxes {
      margin-top: 2px;
    }

    .sum-online-info {
      margin-top: 4px;
    }

    .spinner-container {
      position: relative;
      width: 40px;
      height: 28px;
    }

    .spinner {
      border: 3px solid var(--primary-color);
      border-top-color: transparent;
      right: 0;
    }
  }

  .sum-indicator {
    background: white;
    border-radius: 2px;
    height: 3px;
    left: 50%;
    opacity: 0.7;
    position: absolute;
    top: 10px;
    width: 20px;
    transform: translate(-50%, 0);
  }
}
</style>
